import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ── DATA ──────────────────────────────────────────────────────────────────────
// All figures below are pulled from real MetaTrader 5 Strategy Tester exports
// (XAUUSD, M1, fixed lot) and from a rule-for-rule simulation of FTMO's
// published 2-Step evaluation. See /screenshots/reports/ for the source files.

const STATS = [
  { value: '71%',    label: 'Win Rate · 7,993 Trades' },
  { value: '0',      label: 'FTMO Breaches, Ever' },
  { value: '$64,817',label: 'Paid Out Under FTMO' },
  { value: '$81,763',label: '$3,000 Account, Today' },
];

const YEARS = [
  { period: '2023 · Jan–Dec', ret: '+5.0%', dd: '1.85%', trades: '774', note: 'Quietest year on record — small, steady drift, almost no drawdown.' },
  { period: '2024 · Jan–Dec', ret: '+0.8%', dd: '4.40%', trades: '1,338', note: 'Effectively flat. Six up months, six down — never close to a breach.' },
  { period: '2025 · Jan–Dec', ret: '+37.4%', dd: '2.17%', trades: '2,805', note: 'Strongest risk-adjusted year — 37% return on a 2% drawdown.' },
  { period: '2026 · YTD Aug', ret: '+33.4%', dd: '11.60%', trades: '3,058', note: 'A real 3-month drawdown (Apr–Jun), held together with zero breaches.' },
];

const MECHANICS = [
  { num: '01', name: 'Adaptive zone detection', desc: 'A rolling scan of swing highs, lows, and rejection wicks on an ATR-adaptive grid — zone width flexes with volatility instead of a fixed pip count.' },
  { num: '02', name: 'Strength scoring', desc: 'Every zone is graded 1–4 on touch count, tightness, and duration. Thin, unproven zones get skipped entirely.' },
  { num: '03', name: 'Pending breakout orders', desc: 'A buy-stop above resistance, a sell-stop below support. The order waits at the boundary — no chasing price after the fact.' },
  { num: '04', name: 'Dynamic lot sizing', desc: 'Scales on win streaks (1.75x) and loss streaks (2.0x) separately, each capped and self-resetting. Nothing climbs indefinitely.' },
  { num: '05', name: 'Loss-level memory', desc: 'Holds off re-entering near a price that just produced a loss, for a set number of bars, before trying that zone again.' },
  { num: '06', name: 'Session & basket limits', desc: 'Trading confined to defined session windows. A basket caps at four trades on one side before it must close and reset.' },
];

const FAQS = [
  { q: 'Is this backtested or live?', a: 'Every number on this page comes from a real MetaTrader 5 Strategy Tester export run on tick-level historical data — not a projection. The live dashboard screenshot above is from an actual running session. Backtested performance has known limitations (it can\'t fully model slippage, requotes, or broker-specific execution) and does not guarantee future results.' },
  { q: 'Why does the $3,000 account show a bigger drawdown than the $100,000 one?', a: 'Same trades, same fixed lot size, same dollar profit and loss on every trade — the lot size doesn\'t change with account size. But the same dollar swing is a much bigger percentage of a smaller account. The $17,274 drawdown that reads as 9–12% against $100,000 reads as up to 39.5% against the $3,000 account\'s smaller balance at the time.' },
  { q: 'Can a $3,000 account actually run this?', a: 'The lot size in this backtest was sized with a larger account in mind, and margin got genuinely tight in the real $3,000 test — the lowest margin level reached was 138.93%, uncomfortably close to a typical broker\'s stop-out zone. Worth confirming your broker\'s specific margin requirements before running this at $3,000; a smaller lot size would be the safer starting point.' },
  { q: 'What happens if I use dynamic autolot sizing instead of a fixed lot?', a: 'We tested that too — see the comparison below. Letting the lot size scale with a growing balance turns a system with zero breaches in 2.6 years into one that blew 245 out of 245 funded accounts. The fixed base lot is not a minor setting; it\'s the reason this account has stayed funded.' },
];

// ── EQUITY CHART ──────────────────────────────────────────────────────────────
// Real weekly closing balance, $100,000 account, Jan 2023 – Aug 2026 (190 points)
const EQUITY_LINE = "M 0.0,279.1 L 5.3,278.0 L 10.6,277.0 L 15.9,277.5 L 21.2,278.6 L 26.5,279.3 L 31.7,278.4 L 37.0,276.8 L 42.3,277.2 L 47.6,277.7 L 52.9,278.5 L 58.2,274.3 L 63.5,271.1 L 68.8,271.1 L 74.1,270.8 L 79.4,272.2 L 84.7,271.6 L 89.9,269.6 L 95.2,268.2 L 100.5,269.8 L 105.8,270.2 L 111.1,269.7 L 116.4,267.5 L 121.7,267.8 L 127.0,268.7 L 132.3,268.4 L 137.6,268.3 L 142.9,268.5 L 148.1,269.3 L 153.4,269.7 L 158.7,269.9 L 164.0,270.9 L 169.3,271.1 L 174.6,271.0 L 179.9,271.6 L 185.2,271.4 L 190.5,271.4 L 195.8,272.0 L 201.1,272.0 L 206.3,271.3 L 211.6,271.4 L 216.9,270.9 L 222.2,269.2 L 227.5,268.0 L 232.8,267.7 L 238.1,267.8 L 243.4,267.2 L 248.7,266.5 L 254.0,265.9 L 259.3,265.4 L 264.6,264.4 L 269.8,264.3 L 275.1,265.0 L 280.4,262.8 L 285.7,262.3 L 291.0,263.9 L 296.3,265.3 L 301.6,265.3 L 306.9,264.9 L 312.2,265.9 L 317.5,265.9 L 322.8,265.5 L 328.0,265.0 L 333.3,265.3 L 338.6,264.9 L 343.9,264.2 L 349.2,265.8 L 354.5,266.3 L 359.8,267.1 L 365.1,268.7 L 370.4,267.0 L 375.7,267.4 L 381.0,265.0 L 386.2,264.5 L 391.5,262.9 L 396.8,260.5 L 402.1,260.0 L 407.4,260.8 L 412.7,257.5 L 418.0,257.2 L 423.3,256.0 L 428.6,253.0 L 433.9,251.7 L 439.2,252.9 L 444.4,251.7 L 449.7,251.1 L 455.0,253.9 L 460.3,253.3 L 465.6,254.2 L 470.9,254.4 L 476.2,254.7 L 481.5,254.0 L 486.8,253.1 L 492.1,252.7 L 497.4,254.2 L 502.6,253.7 L 507.9,255.6 L 513.2,260.0 L 518.5,257.9 L 523.8,256.7 L 529.1,256.2 L 534.4,262.4 L 539.7,262.2 L 545.0,261.6 L 550.3,262.5 L 555.6,263.7 L 560.8,263.1 L 566.1,263.2 L 571.4,263.6 L 576.7,262.8 L 582.0,264.9 L 587.3,262.1 L 592.6,260.3 L 597.9,261.1 L 603.2,260.2 L 608.5,263.0 L 613.8,261.8 L 619.0,262.2 L 624.3,254.1 L 629.6,249.2 L 634.9,247.0 L 640.2,240.2 L 645.5,238.9 L 650.8,235.8 L 656.1,236.1 L 661.4,236.9 L 666.7,234.1 L 672.0,235.8 L 677.2,230.2 L 682.5,225.8 L 687.8,226.3 L 693.1,225.2 L 698.4,223.0 L 703.7,223.3 L 709.0,222.0 L 714.3,221.9 L 719.6,226.0 L 724.9,225.5 L 730.2,226.6 L 735.4,225.3 L 740.7,223.6 L 746.0,221.7 L 751.3,220.7 L 756.6,216.1 L 761.9,215.8 L 767.2,212.7 L 772.5,205.1 L 777.8,191.0 L 783.1,185.3 L 788.4,183.0 L 793.7,179.8 L 798.9,174.9 L 804.2,172.8 L 809.5,169.4 L 814.8,164.3 L 820.1,161.8 L 825.4,160.5 L 830.7,155.8 L 836.0,146.2 L 841.3,150.0 L 846.6,137.7 L 851.9,115.5 L 857.1,83.8 L 862.4,68.6 L 867.7,67.8 L 873.0,58.8 L 878.3,63.7 L 883.6,55.3 L 888.9,45.0 L 894.2,23.7 L 899.5,20.7 L 904.8,28.4 L 910.1,36.3 L 915.3,39.6 L 920.6,38.0 L 925.9,35.8 L 931.2,42.9 L 936.5,44.0 L 941.8,47.4 L 947.1,56.8 L 952.4,61.9 L 957.7,66.0 L 963.0,64.6 L 968.3,63.9 L 973.5,60.7 L 978.8,61.9 L 984.1,61.1 L 989.4,64.2 L 994.7,62.2 L 1000.0,60.3";
const EQUITY_AREA = EQUITY_LINE + " L 1000,300 L 0,300 Z";

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function QTraderResultsPage() {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  useEffect(() => {
    if (window.plausible) window.plausible('Strategy Tester Page View');
  }, []);

  const trackEvent = (eventName, props = {}) => {
    if (window.plausible) window.plausible(eventName, { props });
  };

  const goLicense = () => {
    trackEvent('License Click', { product: 'Q-Trader EA', source: 'Strategy Tester Page' });
    window.location.href = 'mailto:qireagency@gmail.com?subject=Q-Trader%20EA%20%E2%80%94%20License%20(From%20%24299%2Fmo)';
  };

  return (
    <div style={{ background: 'var(--black)', minHeight: '100vh' }}>

      {/* ── LIGHT HEADER (this is a focused proof page, not the full site nav) ── */}
      <nav className="q-nav">
        <div className="q-nav-inner">
          <div className="q-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <div className="q-logo-mark">Q</div>
            <span className="q-logo-text">Qire<span>Agency</span></span>
          </div>
          <button className="q-nav-login" onClick={() => navigate('/')}>← All Products</button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <div className="q-hero">
        <div className="q-hero-badge"><div className="q-badge-dot" />Q-Trader EA · Verified 2023–2026</div>
        <h1 className="q-hero-title">
          A trading engine that <span className="teal">survived the boring years</span>
          <span className="line2">Not just the good months.</span>
        </h1>
        <p className="q-hero-sub">Most track records only show you the strong stretch. This one has three and a half years of real backtested history — including two flat, unremarkable years — and has never once breached a prop firm's risk rules.</p>
        <div className="q-hero-actions">
          <button className="q-btn-primary" onClick={goLicense}>Get License · $299/mo</button>
          <button className="q-btn-secondary" onClick={() => document.getElementById('record')?.scrollIntoView({ behavior: 'smooth' })}>See the Full Record</button>
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="q-stats-bar">
        <div className="q-stats-inner">
          {STATS.map((s, i) => (
            <div key={s.label} style={{ display: 'contents' }}>
              <div className="q-stat"><div className="q-stat-value">{s.value}</div><div className="q-stat-label">{s.label}</div></div>
              {i < STATS.length - 1 && <div className="q-stat-divider" />}
            </div>
          ))}
        </div>
      </div>

      {/* ── EQUITY CHART ── */}
      <section className="q-section">
        <div className="q-inner">
          <div className="q-mockup" style={{ maxWidth: 'none' }}>
            <div className="q-mock-bar">
              <div className="q-mock-dot" style={{ background: '#ff5f57' }} /><div className="q-mock-dot" style={{ background: '#febc2e' }} /><div className="q-mock-dot" style={{ background: '#28c840' }} />
              <span className="q-mock-ttl">Q-Trader EA · Equity Curve · $100,000 Account</span>
              <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: '.6rem', color: 'var(--teal)' }}>$177,324 <span style={{ color: 'var(--white-dimmer)' }}>(+77.3%)</span></span>
            </div>
            <div className="q-mock-body" style={{ padding: '1.4rem 1.2rem .6rem' }}>
              <div style={{ position: 'relative', width: '100%', height: '280px' }}>
                <svg viewBox="0 0 1000 300" preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'block' }}>
                  <defs>
                    <linearGradient id="qtFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00c9a7" stopOpacity="0.22" />
                      <stop offset="100%" stopColor="#00c9a7" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <line x1="0" y1="279.1" x2="1000" y2="279.1" stroke="#252530" strokeWidth="1" strokeDasharray="2,4" vectorEffect="non-scaling-stroke" />
                  <path d={EQUITY_AREA} fill="url(#qtFill)" />
                  <path d={EQUITY_LINE} fill="none" stroke="#00c9a7" strokeWidth="2.4" vectorEffect="non-scaling-stroke" />
                  <circle cx="1000" cy="60.3" r="5" fill="#00c9a7" />
                </svg>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-mono)', fontSize: '.62rem', color: 'var(--white-dimmer)', padding: '.5rem .1rem 0' }}>
                <span>2023</span><span>2024</span><span>2025</span><span>2026</span><span>Today</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" className="q-section alt">
        <div className="q-inner">
          <div className="q-products-intro">
            <div className="q-eyebrow">The Strategy</div>
            <h2 className="q-section-title">Trade the structure, not a prediction</h2>
            <p className="q-section-desc">Q-Trader EA doesn't guess where gold is going next. It maps where price has actually been consolidating, scores how strong that structure is, and lets a pending order decide when a breakout is real.</p>
          </div>
          <div className="q-port-grid">
            {MECHANICS.map(m => (
              <div key={m.num} className="q-case">
                <div className="q-case-plat"><span>{m.num}</span></div>
                <div className="q-case-title">{m.name}</div>
                <div className="q-case-quote">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRACK RECORD ── */}
      <section id="record" className="q-section">
        <div className="q-inner">
          <div className="q-products-intro">
            <div className="q-eyebrow">Verified Track Record</div>
            <h2 className="q-section-title">Four years, including the two nobody shows you</h2>
            <p className="q-section-desc">Every number below is from MetaTrader 5 Strategy Tester runs on tick-level XAUUSD M1 data, base lot held fixed, $100,000 starting balance.</p>
          </div>
          <div className="q-port-grid">
            {YEARS.map(y => (
              <div key={y.period} className="q-case">
                <div className="q-case-plat"><span>↗</span>{y.period}</div>
                <div className="q-case-val">{y.ret}</div>
                <div className="q-case-title">DD {y.dd} · {y.trades} trades</div>
                <div className="q-case-quote">{y.note}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '2rem', padding: '1.6rem 1.8rem', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ fontSize: '.92rem', color: 'var(--white-dim)', maxWidth: '560px' }}>Run as one continuous account since Jan 1, 2023: $77,323.50 net profit on $100,000, across 7,993 trades — one uninterrupted equity curve, not four separate lucky windows.</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 800, color: 'var(--teal)', whiteSpace: 'nowrap' }}>+77.3%</div>
          </div>
        </div>
      </section>

      {/* ── TWO PATHS ── */}
      <section id="paths" className="q-section alt">
        <div className="q-inner">
          <div className="q-products-intro">
            <div className="q-eyebrow">Two Ways To Run It</div>
            <h2 className="q-section-title">License a prop firm challenge, or start on your own</h2>
            <p className="q-section-desc">Same trades, same fixed base lot, both replayed against the identical trade history — starting Jan 1, 2023.</p>
          </div>
          <div className="q-products-list">
            <div className="q-product-row">
              <div className="q-prod-col">
                <span className="q-product-tag">Prop Firm Path</span>
                <div className="q-product-name">FTMO, Tested Against The Real Rules</div>
                <div className="q-product-tagline">10%/5% targets · 5% daily loss · 10% max loss · 80/20 split</div>
              </div>
              <div className="q-prod-col">
                <p className="q-product-desc">We replayed the entire trade history against FTMO's actual 2-Step evaluation. One challenge purchase, ever — never repeated.</p>
                <div className="q-product-pills">
                  <span className="q-product-pill">0 breaches</span>
                  <span className="q-product-pill">Funded since May 2025</span>
                  <span className="q-product-pill">23 payout cycles</span>
                  <span className="q-product-pill">$64,817 paid out</span>
                </div>
              </div>
              <div className="q-product-action">
                <div className="q-product-status"><div className="q-status-dot" /><span className="q-status-text live">Funded — Live</span></div>
                <div className="q-product-price">$163,392 today</div>
              </div>
            </div>
            <div className="q-product-row">
              <div className="q-prod-col">
                <span className="q-product-tag">Independent Path</span>
                <div className="q-product-name">Your Own $3,000, Compounding On Its Own</div>
                <div className="q-product-tagline">No challenge, no rules — real account, real margin</div>
              </div>
              <div className="q-prod-col">
                <p className="q-product-desc">The identical trade history, rebased to a real $3,000 start with no reset. $8,559 by end of 2023, $46,192 by end of 2025.</p>
                <div className="q-product-pills">
                  <span className="q-product-pill">Lowest point $2,614</span>
                  <span className="q-product-pill">Max drawdown 39.5%</span>
                  <span className="q-product-pill">Margin level 138.93%</span>
                </div>
              </div>
              <div className="q-product-action">
                <div className="q-product-status"><div className="q-status-dot building" /><span className="q-status-text building">Margin runs tight</span></div>
                <div className="q-product-price">$81,763 today</div>
              </div>
            </div>
          </div>
          <p style={{ fontSize: '.85rem', color: 'var(--white-dimmer)', maxWidth: '640px', marginTop: '1.4rem' }}>One caveat on the independent path: the base lot in this backtest was sized with a larger account in mind. The real $3,000 test's lowest margin level, 138.93%, ran uncomfortably close to a typical broker's stop-out zone — worth confirming your broker's requirements, or starting with a smaller lot, before running this at $3,000.</p>
        </div>
      </section>

      {/* ── LIVE PROOF ── */}
      <section id="proof" className="q-section">
        <div className="q-inner">
          <div className="q-products-intro">
            <div className="q-eyebrow">See It Running</div>
            <h2 className="q-section-title">Not a claim — a live dashboard</h2>
            <p className="q-section-desc">Every figure on this page traces back to a real Strategy Tester export or a live dashboard reading.</p>
          </div>
          <div className="q-mockup" style={{ maxWidth: 'none' }}>
            <div className="q-mock-bar">
              <div className="q-mock-dot" style={{ background: '#ff5f57' }} /><div className="q-mock-dot" style={{ background: '#febc2e' }} /><div className="q-mock-dot" style={{ background: '#28c840' }} />
              <span className="q-mock-ttl">Q-Trader EA · Live Session</span>
              <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: '.46rem', color: 'var(--teal)' }}>● LIVE</span>
            </div>
            <img src="/screenshots/q-trader-dashboard.png" alt="Q-Trader EA live dashboard on XAUUSD M1" style={{ width: '100%', display: 'block' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.6rem', marginTop: '.9rem', fontSize: '.82rem', color: 'var(--white-dimmer)' }}>
            <span>The live dashboard, mid-session — active zones, daily loss budget remaining, win/loss sequence state.</span>
            <span>
              <a href="/screenshots/reports/report-2023.png" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)', textDecoration: 'none' }}>2023</a> ·{' '}
              <a href="/screenshots/reports/report-2024.png" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)', textDecoration: 'none' }}>2024</a> ·{' '}
              <a href="/screenshots/reports/report-2025.png" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)', textDecoration: 'none' }}>2025</a> ·{' '}
              <a href="/screenshots/reports/report-2024-2026.png" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)', textDecoration: 'none' }}>2024–26</a> ·{' '}
              <a href="/screenshots/reports/report-3k-2023-2026.png" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)', textDecoration: 'none' }}>$3K, 2023–26</a>
            </span>
          </div>
        </div>
      </section>

      {/* ── WHY FIXED LOT ── */}
      <section className="q-section alt">
        <div className="q-inner">
          <div className="q-products-intro">
            <div className="q-eyebrow">Why The Base Lot Stays Fixed</div>
            <h2 className="q-section-title">The one setting that decides pass or fail</h2>
            <p className="q-section-desc">We ran the identical dynamic lot sizing engine two ways — base lot fixed, and base lot left to scale with balance. Same cycling, same caps. Only the base changed.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: 'var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }} className="qt-compare">
            <div style={{ background: 'var(--surface)', padding: '1.8rem 1.7rem', boxShadow: 'inset 0 0 0 1px rgba(0,201,167,0.12)' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.68rem', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--teal)', marginBottom: '1.2rem' }}>Fixed base lot — what we run</div>
              {[['Worst single day', '-$2,359'], ['Daily-loss breaches', '0'], ['Challenges needed', '1'], ['Funded accounts survived', 'Still funded, 14+ mo'], ['Net result on $100k', '+$59,459']].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '.65rem 0', borderTop: '1px solid rgba(0,201,167,0.14)', fontSize: '.88rem', color: 'var(--white-dim)' }}>
                  <span>{k}</span><span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--teal)' }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ background: 'var(--surface)', padding: '1.8rem 1.7rem', boxShadow: 'inset 0 0 0 1px rgba(245,166,35,0.1)' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.68rem', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--amber)', marginBottom: '1.2rem' }}>Balance-scaling base lot</div>
              {[['Worst single day', '-$423,800'], ['Daily-loss breaches', '527'], ['Challenges needed', '531'], ['Funded accounts survived', '0 of 245'], ['Net result on $100k', '-$307,980']].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '.65rem 0', borderTop: '1px solid var(--border)', fontSize: '.88rem', color: 'var(--white-dim)' }}>
                  <span>{k}</span><span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--amber)' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
          <p style={{ fontSize: '.88rem', color: 'var(--white-dim)', maxWidth: '680px', marginTop: '1.4rem', lineHeight: 1.7 }}>Letting the base lot scale looks appealing at first — it's how a small account turns into headline numbers. But the sizing cycle applies its multipliers on top of whatever the base is. Once that base has compounded into the millions, a routine loss sequence gets multiplied against a position already too large for a fixed-dollar-limit account to survive.</p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="q-section">
        <div className="q-inner">
          <div className="q-faq-layout">
            <div>
              <div className="q-eyebrow">FAQ</div>
              <h2 className="q-section-title">Common questions</h2>
              <p className="q-section-desc">Answers to what people ask before licensing Q-Trader EA.</p>
            </div>
            <div className="q-faq-list">
              {FAQS.map((item, i) => (
                <div key={i} className="q-faq-item">
                  <button className="q-faq-trigger" onClick={() => setFaqOpen(faqOpen === i ? null : i)}>
                    {item.q}<span className={`q-faq-icon${faqOpen === i ? ' open' : ''}`}>+</span>
                  </button>
                  <div className={`q-faq-body${faqOpen === i ? ' open' : ''}`}>{item.a}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CLOSE ── */}
      <section className="q-section alt" style={{ textAlign: 'center' }}>
        <div className="q-inner">
          <h2 className="q-section-title" style={{ margin: '0 auto 1rem', maxWidth: '560px' }}>One verified engine. Two ways in.</h2>
          <p className="q-section-desc" style={{ margin: '0 auto 2rem' }}>License it for a prop firm challenge and let it fund itself, or start on your own $3,000 and let it compound.</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="q-btn-primary" onClick={goLicense}>Get License · $299/mo</button>
            <button className="q-btn-secondary" onClick={() => navigate('/')}>← Back to Qire Agency</button>
          </div>
        </div>
      </section>

      {/* ── DISCLAIMER + FOOTER ── */}
      <div className="q-inner" style={{ padding: '2rem 2.5rem' }}>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: '.66rem', lineHeight: 1.8, color: 'var(--white-dimmer)' }}>
          ALL PERFORMANCE FIGURES ARE DERIVED FROM METATRADER 5 STRATEGY TESTER BACKTESTS ON HISTORICAL TICK DATA AND FROM A SIMULATION OF FTMO'S PUBLISHED EVALUATION RULES — NOT LIVE TRADING RESULTS, AND NOT A GUARANTEE FROM FTMO OR ANY PROP FIRM. BACKTESTED PERFORMANCE DOES NOT ACCOUNT FOR SLIPPAGE, REQUOTES, BROKER-SPECIFIC EXECUTION, OR RULE CHANGES. PAST PERFORMANCE DOES NOT GUARANTEE FUTURE RESULTS. TRADING LEVERAGED PRODUCTS CARRIES RISK OF LOSS.
        </p>
      </div>
      <footer className="q-footer">
        <div className="q-footer-inner">
          <div className="q-footer-brand">
            <div className="q-footer-mark">Q</div>
            <span className="q-footer-name">Qire Agency</span>
          </div>
          <span className="q-footer-copy">© 2026 Qire Agency · Q-Trader EA</span>
          <div className="q-footer-links">
            <a href="mailto:qireagency@gmail.com">Email</a>
            <a href="/">Home</a>
          </div>
        </div>
      </footer>

    </div>
  );
}